import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the Minimal Pair wizard.
//
// The preview mirrors FRAME 0, and for this template frame 0 is unusually
// literal: BOTH word cards are already painted, with their differing graphemes
// already tinted, their IPA chips, glosses and mouth-hint panels all on screen.
// So every applier here REPAINTS something that is already visible. Three
// consequences worth stating, because each one is a law rather than a style
// choice:
//
//   1. Nothing in this controller may touch `opacity`, ever. The template has no
//      fade-in and no fade-out; the inactive-card dim in the render is a colour
//      mix, and the diff tint is a colour on a subset of letters.
//   2. No card is lit here. At frame 0 the walk has not started and the two
//      words are co-equal; in the ear-test beat that same "no card lit" state is
//      the no-tell invariant the renderer is tested on. The preview states the
//      same thing by simply never having a highlight to apply.
//   3. The WORDS are deliberately NOT live-bound. The tint on both cards is
//      derived from both words together, and that derivation already exists
//      twice — the renderer's utils/diffSpans.ts and the server mirror in
//      live_preview_component.rb. A third copy in JS is precisely how a preview
//      starts lying about the render, so word edits repaint on save (the T1/T2
//      posture for content rows). Everything that does NOT feed the tint —
//      hook, phonemes, glosses, hints, mouth shapes, prompt, CTA, language,
//      theme — updates live.
//
// The mouth glyphs are swapped by writing ONE attribute: the server renders the
// whole SVG library into each panel and `data-glyph` selects which drawing is
// displayed (CSS in live_preview_component.html.erb). That keeps the drawings in
// exactly two places — the ERB and the renderer's MouthGlyph.tsx — instead of
// three.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed by
// both legs), so the preview can never drift from what the renderer paints. The
// IPA face on the phoneme chips is NOT part of that catalog and is never swapped
// by the type-pair choice: IPA is notation, not typography.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value normalisation
// from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "frame", "flag", "languageLabel", "hookText",
    "wordA", "wordB",
    "phonemeA", "phonemeB", "glossA", "glossB", "hintA", "hintB",
    "glyphA", "glyphB",
    "quizPrompt", "ctaText",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    quiz_prompt:          { apply: "applyQuizPrompt" },
    cta_text:             { apply: "applyCtaText" },
    phoneme_a:            { apply: "applySideField" },
    phoneme_b:            { apply: "applySideField" },
    gloss_a:              { apply: "applySideField" },
    gloss_b:              { apply: "applySideField" },
    hint_a:               { apply: "applySideField" },
    hint_b:               { apply: "applySideField" },
    glyph_a:              { apply: "applyGlyph" },
    glyph_b:              { apply: "applyGlyph" },
    target_language_code: { apply: "applyTargetLanguage" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  // The hook is drawn the way the renderer draws it: language tags are an
  // authoring instruction for the take splitter, never ink. (The authoring law
  // says this template's hook carries no tags at all — stripping anyway means a
  // legacy payload still draws clean.)
  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  applyQuizPrompt() { this.setText("quizPrompt", this.fieldValue("quiz_prompt")) }
  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  // phoneme_a / gloss_b / hint_a … → phonemeA / glossB / hintA. One applier for
  // six fields: the per-side chrome is the same repaint on both cards, and the
  // field name already carries which side it belongs to.
  applySideField({ field, value }) {
    this.setText(targetNameForSideField(field), value)
  }

  // Swaps the visible drawing by attribute. `none` matches no `svg[data-glyph-key]`
  // rule, so the panel falls back to hint text alone — the honest degrade, and
  // also where an unknown stored value lands.
  applyGlyph({ field, value }) {
    const name = targetNameForSideField(field)
    const has = `has${capitalize(name)}Target`
    if (this[has]) this[`${name}Target`].dataset.glyph = value || "none"
  }

  applyTargetLanguage({ value }) {
    this.setText("languageLabel", LANGUAGE_NAMES[value] || (value || "").toUpperCase())
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[value] || "🏳️"
  }

  // The palette drives every ink on the frame: the card words take `--p-text`,
  // the diff spans and the chip borders take `--p-accent`, the glosses take
  // `--p-muted`. Swapping the custom properties repaints all of them without
  // touching a single opacity.
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
  }

  // -- typography / font-size / stroke --------------------------------------

  // The pair words are DISPLAY role alongside the hook — they are the hero of
  // the frame. The chips are excluded on purpose: their face is fixed IPA.
  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    const display = []
    const body = []
    if (this.hasHookTextTarget) display.push(this.hookTextTarget)
    for (const el of this.wordTargets()) display.push(el)
    for (const el of this.bodyTargets()) body.push(el)
    applyTypographyToElements({ display, body }, spec, this.fieldValue("italic_override") || "auto")
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    const displayPairs = []
    if (this.hasHookTextTarget) displayPairs.push(sizedPair(this.hookTextTarget, 13))
    for (const el of this.wordTargets()) displayPairs.push(sizedPair(el, 34))
    applyFontSizeScale(displayPairs, displayScale)

    const bodyPairs = this.bodyTargets().map((el) => sizedPair(el, 10))
    applyFontSizeScale(bodyPairs, bodyScale)
  }

  // Stroke rides the opted-in elements only — the hook + the two words. Chips,
  // glosses, hints and the CTA pill sit on the theme surface and skip, mirroring
  // the renderer's applyStroke: false on those roles.
  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- preview DOM helpers --------------------------------------------------

  wordTargets() {
    return [
      this.hasWordATarget ? this.wordATarget : null,
      this.hasWordBTarget ? this.wordBTarget : null,
    ].filter(Boolean)
  }

  // Everything on the theme surface: the chips, the glosses, the hints, the
  // ear-test prompt and the CTA pill.
  bodyTargets() {
    return [
      "phonemeA", "phonemeB", "glossA", "glossB", "hintA", "hintB", "quizPrompt", "ctaText",
    ]
      .filter((name) => this[`has${capitalize(name)}Target`])
      .map((name) => this[`${name}Target`])
  }

  setText(targetName, value) {
    const has = `has${capitalize(targetName)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

// `phoneme_a` → `phonemeA`, `gloss_b` → `glossB`, `glyph_a` → `glyphA`.
function targetNameForSideField(field) {
  const [base, side] = field.split("_")
  return `${base}${(side || "").toUpperCase()}`
}

function capitalize(name) {
  return name[0].toUpperCase() + name.slice(1)
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) split the spoken take;
// they are never drawn. Mirrors LanguageRuns.strip_tags on the Ruby side.
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
}

const LANGUAGE_NAMES = {
  "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
  "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
  "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
  "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
